/* ActivityMonitorAddOn.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;

class ActivityMonitorAddOn extends EventEmitter {
    constructor(logger, userSession, userFeedback, steveCore) {
        super();
        this._logger = logger;
        this._userSession = userSession;
        this._userFeedback = userFeedback;
        this._steve = steveCore ? steveCore : window.steve;

        // Setting polling interval to default 60000 initially
        this._options = _.defaults(
            /*options || */ {},
            {
                pollingInterval: 60000,
                warningInactivityPolls: 8,
                maxInactivityPolls: 20,
            }
        );

        this._pollingInterval = this._options.pollingInterval;
        this._maxInactivityPolls = this._options.maxInactivityPolls;
        this._warningInactivityPolls = this._options.warningInactivityPolls;

        this._pollingFunction = null;
        this._isPolling = false;

        this._pollAndCheck = this._pollAndCheck.bind(this);
        this._resetInactivityPolls = this._resetInactivityPolls.bind(this);
        this.togglePolling = this.togglePolling.bind(this);
    }

    registerListeners() {
        this._userSession.on(
            "Authenticated",
            (this._onAuthenticated = this._onAuthenticated.bind(this))
        );
        this._userSession.on(
            "UnAuthenticated",
            (this._onUnAuthenticated = this._onUnAuthenticated.bind(this))
        );
    }

    unregisterListeners() {
        this._userSession.removeListener("Authenticated", this._onAuthenticated);
        this._userSession.removeListener("UnAuthenticated", this._onUnAuthenticated);
    }

    shutdown() {
        return Promise.resolve(this.unregisterListeners());
    }

    initialize(/*steve, addOnOptions*/) {}

    startUp(/*steve, addOnOptions*/) {
        this.registerListeners();
    }

    _updateMaxInactivityPollsFromSettings() {
        const that = this;
        const httpRequest = new XMLHttpRequest();
        if (!httpRequest) {
            this._logger.warn(
                {error: "Cannot create XMLHttp instance"},
                "UpdateMaxInactivityPollsFromSettings::Cannot make the HTTP request"
            );
            return;
        }
        const requestPath = "/system/getSystemPreferences";
        httpRequest.onreadystatechange = updatePollingInterval;
        httpRequest.open("GET", requestPath);
        httpRequest.send();

        function updatePollingInterval() {
            try {
                if (httpRequest.readyState === XMLHttpRequest.DONE) {
                    if (httpRequest.status === 200) {
                        const systemPreferences = JSON.parse(httpRequest.responseText);
                        that._maxInactivityPolls = _.get(
                            systemPreferences,
                            "inactivityTimeout",
                            that._maxInactivityPolls
                        );
                    } else {
                        that._logger.warn(
                            "UpdateMaxInactivityPollsFromSettings::HTTP request failed"
                        );
                    }
                    that.startPolling();
                }
            } catch (error) {
                that._logger.warn({error}, "UpdateMaxInactivityPollsFromSettings::Request failed");
                that.startPolling();
            }
        }
    }

    _onAuthenticated(/*user*/) {
        this._updateMaxInactivityPollsFromSettings();
    }

    _onUnAuthenticated() {
        this.stopPolling();
    }

    _getPollingInterval() {
        return this._pollingInterval;
    }

    get pollingInterval() {
        return this._getPollingInterval();
    }

    _setPollingInterval(intervalMs) {
        this._pollingInterval = intervalMs;
    }

    set pollingInterval(intervalMs) {
        this._setPollingInterval(intervalMs);
    }

    _getMaxInactivityPolls() {
        return this._maxInactivityPolls;
    }

    get maxInactivityPolls() {
        return this._getMaxInactivityPolls();
    }

    _setMaxInactivityPolls(newMaxPolls) {
        this._maxInactivityPolls = newMaxPolls;
    }

    set maxInactivityPolls(newMaxPolls) {
        this._setMaxInactivityPolls(newMaxPolls);
    }

    _resetInactivityPolls() {
        if (
            (this._userSession.isConnectedAndAuthenticated &&
                this._userSession.isConnectedAndAuthenticated()) ||
            (this._userSession._connHandler &&
                this._userSession._connHandler.isAuthenticated &&
                this._userSession._connHandler.isAuthenticated())
        ) {
            window.localStorage.setItem(
                "Steve.session.expireTokenTime",
                Date.now() + this._pollingInterval * this._maxInactivityPolls
            );
        }
    }

    _pollAndCheck() {
        let expireTokenTime = window.localStorage.getItem("Steve.session.expireTokenTime");
        expireTokenTime = expireTokenTime ? parseInt(expireTokenTime, 10) : 0;

        let timeDifference = Date.now() - expireTokenTime;

        if (Date.now() > expireTokenTime) {
            this.emit("timeout");

            const token = this._userSession.token || localStorage.getItem("Steve.session.token");
            const logoutUrl = `${window.origin}/session/logout/${token}%2Ftimeout?skipRedirect=true`;

            this.stopPolling();
            localStorage.clear();

            this._userSession.removeAllListeners("UnAuthenticated");
            fetch(logoutUrl, {method: "GET"}).then((response) => {
                if (!response.ok) {
                    console.error("Failed to log out:", response.statusText);
                }

                this._steve.controlAppNav._onUnAuthenticated("timeout");
            });
        } else if (
            this._pollingInterval !== 0 &&
            timeDifference / this._pollingInterval >= this._warningInactivityPolls
        ) {
            this.emit("warning");
        }
    }

    startPolling() {
        if (
            !this._isPolling &&
            ((this._userSession.isConnectedAndAuthenticated &&
                this._userSession.isConnectedAndAuthenticated()) ||
                (this._userSession._connHandler &&
                    this._userSession._connHandler.isAuthenticated &&
                    this._userSession._connHandler.isAuthenticated()))
        ) {
            window.localStorage.setItem(
                "Steve.session.expireTokenTime",
                Date.now() + this._pollingInterval * this._maxInactivityPolls
            );
            document.body.addEventListener("click", this._resetInactivityPolls, true);
            document.body.addEventListener("keydown", this._resetInactivityPolls, true);
            this._pollingFunction = setInterval(this._pollAndCheck, this._pollingInterval);
            this._isPolling = true;
        }
    }

    stopPolling() {
        if (this._pollingFunction) {
            clearInterval(this._pollingFunction);
            document.body.removeEventListener("click", this._resetInactivityPolls);
            document.body.removeEventListener("keydown", this._resetInactivityPolls);
            this._isPolling = false;
        }
    }

    togglePolling(state) {
        if (state) {
            this.startPolling();
        } else {
            this.stopPolling();
        }
    }
}

module.exports = ActivityMonitorAddOn;
module.exports.$inject = [
    "$teve.logger",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.core",
];
